import React from 'react';
import { View, ScrollView, StyleSheet, Text } from 'react-native';
import { Title, Paragraph, Button, Card, useTheme } from 'react-native-paper';

export default function SplashScreen({ navigation }: any) {
  const theme = useTheme();

  return (
    <ScrollView style={[styles.container, { backgroundColor: '#F8FAFC' }]}>
      {/* Hero Section */}
      <View style={styles.heroSection}>
        <View style={styles.heroContent}>
          <Title style={styles.heroTitle}>
            Where Every Sip Tells a Story
          </Title>
          <Paragraph style={styles.heroSubtitle}>
            Welcome to RumDB, the world's most comprehensive destination for rum culture, 
            tiki traditions, and the art of the perfect cocktail. Here, we celebrate the 
            spirit of adventure that flows through every bottle, every recipe, and every 
            moment shared over a well-crafted drink.
          </Paragraph>
          
          <View style={styles.heroButtons}>
            <Button
              mode="contained"
              style={styles.primaryButton}
              onPress={() => navigation.navigate('App')}
            >
              Explore Our Cocktail Collection
            </Button>
            <Button
              mode="outlined"
              style={styles.secondaryButton}
              labelStyle={styles.secondaryButtonText}
              onPress={() => navigation.navigate('App', { screen: 'Blog' })}
            >
              Read My Journey
            </Button>
          </View>
        </View>
      </View>

      {/* Personal Story Section */}
      <View style={styles.storySection}>
        <Card style={styles.storyCard}>
          <Card.Content style={styles.storyContent}>
            <Title style={styles.storyTitle}>
              Why This Matters to Me
            </Title>
            <Paragraph style={styles.storyText}>
              Four years ago, I took a sip that changed everything. What began as a casual 
              interest in rum blossomed into a full-blown obsession... a deep dive into a world 
              I never knew existed. I learned about the nuances of agricole, the rich history 
              of molasses-based rums, the magic of barrel aging, and the distinct terroirs 
              that give each rum its unique character.
            </Paragraph>
            <Paragraph style={styles.storyText}>
              The more I learned, the more I realized the vast, untapped potential of this 
              spirit. It wasn't just about cocktails... it was about culture, history, and the 
              incredible people who dedicate their lives to its creation. This growing fascination, 
              this insatiable curiosity, eventually led me to a realization: there needed to be 
              a better way to explore, categorize, and celebrate this incredible world.
            </Paragraph>
            <Paragraph style={styles.storyText}>
              And so, RumDB was born... a passion project fueled by countless hours of research, 
              late-night tastings, and an ever-deepening appreciation for rum and its vibrant, 
              often overlooked, culture. It's a journey that continues to unfold, with every 
              new distillery discovered, every forgotten cocktail recipe unearthed, and every 
              conversation with fellow enthusiasts.
            </Paragraph>
            <Button
              mode="outlined"
              style={styles.storyButton}
              onPress={() => navigation.navigate('App', { screen: 'Blog' })}
            >
              Read My Full Story
            </Button>
          </Card.Content>
        </Card>
      </View>

      {/* Vision Section */}
      <View style={styles.visionSection}>
        <Card style={styles.visionCard}>
          <Card.Content style={styles.visionContent}>
            <Title style={styles.visionTitle}>
              A Journey Through Time and Taste
            </Title>
            <Paragraph style={styles.visionText}>
              From the golden age of tiki bars in the 1940s to today's craft cocktail 
              renaissance, we're building more than just a database... we're creating a 
              living archive of stories, traditions, and techniques that have shaped 
              the world of rum and tiki culture for generations.
            </Paragraph>
            <Paragraph style={styles.visionText}>
              Every cocktail has a story. Every bar has a history. Every sip carries 
              the weight of tradition and the promise of discovery. At RumDB, we're 
              preserving these stories while writing new chapters in the ongoing saga 
              of rum culture.
            </Paragraph>
          </Card.Content>
        </Card>
      </View>

      {/* Features Section */}
      <View style={styles.featuresSection}>
        <Title style={styles.sectionTitle}>
          What We're Building
        </Title>
        <Paragraph style={styles.sectionSubtitle}>
          A comprehensive platform that honors the past while embracing the future of rum culture
        </Paragraph>
        
        <View style={styles.featuresGrid}>
          <Card style={styles.featureCard}>
            <Card.Content style={styles.featureContent}>
              <Title style={styles.featureTitle}>
                The Ultimate Cocktail Library
              </Title>
              <Paragraph style={styles.featureDescription}>
                Thousands of meticulously curated recipes, each with its own story, 
                technique, and cultural significance. From classic tiki drinks to 
                modern innovations, discover the art and science behind every pour.
              </Paragraph>
            </Card.Content>
          </Card>

          <Card style={styles.featureCard}>
            <Card.Content style={styles.featureContent}>
              <Title style={styles.featureTitle}>
                A World of Tiki Bars & Distilleries
              </Title>
              <Paragraph style={styles.featureDescription}>
                Embark on a global journey to discover the most remarkable rum bars 
                and distilleries. Each location tells a unique story of passion, 
                tradition, and the relentless pursuit of perfection.
              </Paragraph>
            </Card.Content>
          </Card>

          <Card style={styles.featureCard}>
            <Card.Content style={styles.featureContent}>
              <Title style={styles.featureTitle}>
                The Spirit of Tiki Culture
              </Title>
              <Paragraph style={styles.featureDescription}>
                Immerse yourself in the rich tapestry of tiki culture... its history, 
                its legends, its rituals, and its enduring appeal. Learn the stories 
                behind the drinks and the people who made them legendary.
              </Paragraph>
            </Card.Content>
          </Card>

          <Card style={styles.featureCard}>
            <Card.Content style={styles.featureContent}>
              <Title style={styles.featureTitle}>
                A Community of Connoisseurs
              </Title>
              <Paragraph style={styles.featureDescription}>
                Connect with fellow enthusiasts, share your discoveries, and contribute 
                to the collective knowledge of rum culture. Every review, every rating, 
                every story adds to our shared understanding.
              </Paragraph>
            </Card.Content>
          </Card>
        </View>
      </View>

      {/* Alpha Preview */}
      <View style={styles.alphaSection}>
        <Card style={styles.alphaCard}>
          <Card.Content style={styles.alphaContent}>
            <Title style={styles.alphaTitle}>
              Experience the Future Today
            </Title>
            <Paragraph style={styles.alphaDescription}>
              While we craft the complete RumDB experience, you can explore our alpha 
              cocktail database... a carefully curated collection of thousands of recipes, 
              each with its own story to tell. This is just the beginning of what we're 
              building together.
            </Paragraph>
            <Paragraph style={styles.alphaDescription}>
              Our advanced filtering system, inspired by the very essence of tiki culture, 
              allows you to discover new favorites, rediscover classics, and embark on 
              your own journey through the world of rum cocktails.
            </Paragraph>
            <Button
              mode="contained"
              style={styles.alphaButton}
              onPress={() => navigation.navigate('App')}
            >
              Begin Your Journey
            </Button>
          </Card.Content>
        </Card>
      </View>

      {/* Footer - Legacy Section */}
      <View style={styles.footer}>
        <Title style={styles.footerTitle}>
          The Legacy We Honor
        </Title>
        <Paragraph style={styles.footerText}>
          Tiki culture represents more than just a style of drink... it embodies the human 
          desire for escape, adventure, and connection. In the 1940s, as the world emerged 
          from the shadows of war, tiki bars offered a portal to exotic destinations and 
          carefree moments. Today, they continue to serve as sanctuaries where time slows 
          down and stories flow as freely as the drinks.
        </Paragraph>
        <Paragraph style={styles.footerText}>
          We believe that every cocktail is a work of art, every bar is a gallery, and 
          every bartender is a storyteller. At RumDB, we're not just cataloging drinks... 
          we're preserving the spirit of hospitality, the art of presentation, and the 
          magic that happens when people gather to share in the ritual of the cocktail.
        </Paragraph>
        
        <View style={styles.footerStats}>
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>1944</Text>
            <Text style={styles.statLabel}>The Birth of Tiki</Text>
          </View>
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>400+</Text>
            <Text style={styles.statLabel}>Rum Varieties</Text>
          </View>
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>75+</Text>
            <Text style={styles.statLabel}>Years of Tradition</Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  heroSection: {
    minHeight: 600,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#1E293B',
    padding: 32,
  },
  heroContent: {
    alignItems: 'center',
    maxWidth: 800,
  },
  heroTitle: {
    fontSize: 48,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 24,
    color: '#FFFFFF',
    lineHeight: 56,
  },
  heroSubtitle: {
    fontSize: 20,
    textAlign: 'center',
    marginBottom: 48,
    lineHeight: 32,
    color: '#E2E8F0',
    fontWeight: '300',
  },
  heroButtons: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  primaryButton: {
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#F59E0B',
  },
  secondaryButton: {
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 8,
    borderColor: '#FFFFFF',
    borderWidth: 2,
  },
  secondaryButtonText: {
    color: '#FFFFFF',
  },
  storySection: {
    padding: 32,
    backgroundColor: '#FFFFFF',
  },
  storyCard: {
    borderRadius: 16,
    elevation: 8,
    backgroundColor: '#FEF3C7',
  },
  storyContent: {
    padding: 32,
  },
  storyTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    marginBottom: 24,
    color: '#92400E',
    textAlign: 'center',
  },
  storyText: {
    fontSize: 18,
    lineHeight: 28,
    marginBottom: 16,
    color: '#78350F',
    textAlign: 'left',
  },
  storyButton: {
    marginTop: 16,
    borderColor: '#92400E',
    borderWidth: 2,
  },
  visionSection: {
    padding: 32,
    backgroundColor: '#F8FAFC',
  },
  visionCard: {
    borderRadius: 16,
    elevation: 8,
    backgroundColor: '#FEF3C7',
  },
  visionContent: {
    padding: 32,
  },
  visionTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    marginBottom: 24,
    color: '#92400E',
    textAlign: 'center',
  },
  visionText: {
    fontSize: 18,
    lineHeight: 28,
    marginBottom: 16,
    color: '#78350F',
    textAlign: 'center',
  },
  featuresSection: {
    padding: 32,
    backgroundColor: '#FFFFFF',
  },
  sectionTitle: {
    fontSize: 36,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
    color: '#1E293B',
  },
  sectionSubtitle: {
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 48,
    lineHeight: 28,
    color: '#64748B',
    fontStyle: 'italic',
  },
  featuresGrid: {
    gap: 24,
  },
  featureCard: {
    marginBottom: 24,
    borderRadius: 16,
    elevation: 4,
    backgroundColor: '#FFFFFF',
  },
  featureContent: {
    padding: 24,
  },
  featureTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 16,
    color: '#1E293B',
  },
  featureDescription: {
    fontSize: 16,
    lineHeight: 24,
    color: '#475569',
  },
  alphaSection: {
    padding: 32,
    backgroundColor: '#F8FAFC',
  },
  alphaCard: {
    borderRadius: 16,
    elevation: 8,
    backgroundColor: '#1E293B',
  },
  alphaContent: {
    padding: 32,
    alignItems: 'center',
  },
  alphaTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    marginBottom: 24,
    textAlign: 'center',
    color: '#FFFFFF',
  },
  alphaDescription: {
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 28,
    color: '#E2E8F0',
  },
  alphaButton: {
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#F59E0B',
    marginTop: 16,
  },
  footer: {
    padding: 32,
    alignItems: 'center',
    backgroundColor: '#1E293B',
  },
  footerTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    marginBottom: 24,
    textAlign: 'center',
    color: '#FFFFFF',
  },
  footerText: {
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 28,
    color: '#E2E8F0',
    maxWidth: 800,
  },
  footerStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 32,
    width: '100%',
  },
  statItem: {
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#F59E0B',
  },
  statLabel: {
    fontSize: 14,
    color: '#94A3B8',
    marginTop: 8,
    textAlign: 'center',
  },
}); 